import React, { createContext, FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { browserHistory } from 'react-router';

import { TCustomWebStylesModelV2, useGetCustomWebStyles } from 'requests/resources/customWebStylesV2';

import { selectCurrentMasterSettings } from '@lib/selectors';

import { currentDomainOwner } from 'utils/constants';

import { CHC_LOGO, CHC_LOGO_WHITE, LUMA_LOGO, LUMA_LOGO_WHITE } from '../constants';

import GREENWAY_LOGO from './partners_logos/greenway.png';
import NEXTGEN_LOGO from './partners_logos/nextgen.png';
import NEXTGEN_LOGO_WHITE from './partners_logos/nextgen_white.png';

const logos = {
  luma: {
    logo: LUMA_LOGO,
    logoWhite: LUMA_LOGO_WHITE,
  },
  nextgen: {
    logo: NEXTGEN_LOGO,
    logoWhite: NEXTGEN_LOGO_WHITE,
  },
  greenway: {
    logo: GREENWAY_LOGO,
    logoWhite: GREENWAY_LOGO,
  },
  chc: {
    logo: CHC_LOGO,
    logoWhite: CHC_LOGO_WHITE,
  },
};

export const defaultCurrentDomainLogo = logos[currentDomainOwner()].logo;
export const defaultCurrentDomainLogoWhite = logos[currentDomainOwner()].logoWhite;

interface BrandingTheme {
  scope: 'cobranding' | 'patientForm' | 'embed' | '';
  enabled: boolean;
}

export interface LogoFile {
  uploadName: string;
  url: string;
}

interface BrandingContextProps {
  brandingThemes: BrandingTheme[];
  isLoading: boolean;
  isCobrandingEnabled: boolean;
  cobrandingLogo: LogoFile | undefined;
  cobrandingFavicon: LogoFile | undefined;
  cobrandingCustomStyles: TCustomWebStylesModelV2 | undefined;
}

const initialContextData: BrandingContextProps = {
  brandingThemes: [],
  isLoading: false,
  isCobrandingEnabled: false,
  cobrandingLogo: undefined,
  cobrandingFavicon: undefined,
  cobrandingCustomStyles: undefined,
};

export const BrandingContext = createContext<BrandingContextProps>(initialContextData);

const BrandingProvider: FC = ({ children }) => {
  const currentMasterSettings = useSelector(selectCurrentMasterSettings);
  const { pathname } = browserHistory.getCurrentLocation();
  const brandingThemes: BrandingTheme[] = useMemo(() => currentMasterSettings?.branding?.themes ?? [], [
    currentMasterSettings?.branding?.themes,
  ]);
  const cobrandingSettings = brandingThemes.find((theme) => theme?.scope === 'cobranding');
  const isCobrandingEnabled = !!cobrandingSettings?.enabled;

  const { data = [], isLoading } = useGetCustomWebStyles({ enabled: isCobrandingEnabled });
  const cobrandingCustomStyles = data?.find(
    (customStyles: any) => customStyles.scope?.includes('cobranding') && customStyles.published,
  );

  const useDarkLogo = pathname.includes('/settings');

  const cobrandingLightLogo = cobrandingCustomStyles?.uploads?.find(
    (upload: any) => upload?.uploadName === 'lightLogo',
  );

  // in case the User didnt upload a darkLogo, we will use the lightLogo
  const cobrandingDarkLogo =
    cobrandingCustomStyles?.uploads?.find((upload: any) => upload?.uploadName === 'darkLogo') ?? cobrandingLightLogo;

  const cobrandingFavicon = cobrandingCustomStyles?.uploads?.find((upload: any) => upload?.uploadName === 'favicon');

  const cobrandingLogo = useDarkLogo ? cobrandingDarkLogo : cobrandingLightLogo;

  const value = useMemo(
    () => ({
      brandingThemes,
      isLoading,
      isCobrandingEnabled,
      cobrandingLogo,
      cobrandingFavicon,
      cobrandingCustomStyles,
    }),
    [brandingThemes, isLoading, isCobrandingEnabled, cobrandingLogo, cobrandingFavicon, cobrandingCustomStyles],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export default BrandingProvider;
